import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where,
  orderBy,
  writeBatch 
} from 'firebase/firestore';
import { db } from '../config/firebase';

const GROUPS_COLLECTION = 'propertyGroups';
const UNITS_COLLECTION = 'propertyUnits';

export const propertyService = {
  // ===== PROPERTY GROUPS (Societies/Towers/Communities) =====
  
  // Create a new property group
  async createPropertyGroup(groupData) {
    try {
      const docRef = await addDoc(collection(db, GROUPS_COLLECTION), {
        ...groupData,
        createdAt: new Date(),
        updatedAt: new Date(),
        status: 'active'
      });
      return { id: docRef.id, ...groupData };
    } catch (error) {
      throw error;
    }
  },

  // Get all property groups for a company
  async getPropertyGroupsByCompany(companyId) {
    try {
      const q = query(
        collection(db, GROUPS_COLLECTION),
        where('companyId', '==', companyId)
      );
      const querySnapshot = await getDocs(q);
      const groups = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return groups.sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.());
    } catch (error) {
      throw error;
    }
  },

  // Get property group by ID
  async getPropertyGroupById(groupId) {
    try {
      const docRef = doc(db, GROUPS_COLLECTION, groupId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Property group not found');
      }
    } catch (error) {
      throw error;
    }
  },

  // Update property group
  async updatePropertyGroup(groupId, updateData) {
    try {
      const docRef = doc(db, GROUPS_COLLECTION, groupId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: new Date()
      });
      return { id: groupId, ...updateData };
    } catch (error) {
      throw error;
    }
  },

  // Delete property group and all its units
  async deletePropertyGroup(groupId) {
    try {
      const batch = writeBatch(db);
      
      // Delete all units in this group
      const unitsQuery = query(
        collection(db, UNITS_COLLECTION),
        where('groupId', '==', groupId)
      );
      const unitsSnapshot = await getDocs(unitsQuery);
      
      unitsSnapshot.docs.forEach((unitDoc) => {
        batch.delete(unitDoc.ref);
      });
      
      // Delete the group
      const groupRef = doc(db, GROUPS_COLLECTION, groupId);
      batch.delete(groupRef);
      
      await batch.commit();
      return true;
    } catch (error) {
      throw error;
    }
  },

  // ===== PROPERTY UNITS =====
  
  // Create a new property unit
  async createPropertyUnit(unitData) {
    try {
      const docRef = await addDoc(collection(db, UNITS_COLLECTION), {
        ...unitData,
        createdAt: new Date(),
        updatedAt: new Date(),
        status: unitData.status || 'available'
      });
      return { id: docRef.id, ...unitData };
    } catch (error) {
      throw error;
    }
  },

  // Get all units in a property group
  async getUnitsByGroup(groupId) {
    try {
      const q = query(
        collection(db, UNITS_COLLECTION),
        where('groupId', '==', groupId)
      );
      const querySnapshot = await getDocs(q);
      const units = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return units.sort((a, b) => a.unitNumber.localeCompare(b.unitNumber));
    } catch (error) {
      throw error;
    }
  },

  // Get all units for a company
  async getUnitsByCompany(companyId) {
    try {
      const q = query(
        collection(db, UNITS_COLLECTION),
        where('companyId', '==', companyId)
      );
      const querySnapshot = await getDocs(q);
      const units = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return units.sort((a, b) => a.unitNumber.localeCompare(b.unitNumber));
    } catch (error) {
      throw error;
    }
  },

  // Get property unit by ID
  async getPropertyUnitById(unitId) {
    try {
      const docRef = doc(db, UNITS_COLLECTION, unitId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Property unit not found');
      }
    } catch (error) {
      throw error;
    }
  },

  // Update property unit
  async updatePropertyUnit(unitId, updateData) {
    try {
      const docRef = doc(db, UNITS_COLLECTION, unitId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: new Date()
      });
      return { id: unitId, ...updateData };
    } catch (error) {
      throw error;
    }
  },

  // Delete property unit
  async deletePropertyUnit(unitId) {
    try {
      const docRef = doc(db, UNITS_COLLECTION, unitId);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      throw error;
    }
  },

  // ===== BULK OPERATIONS =====
  
  // Bulk create property units
  async bulkCreateUnits(unitsData) {
    try {
      const batch = writeBatch(db);
      const createdUnits = [];

      for (const unitData of unitsData) {
        const docRef = doc(collection(db, UNITS_COLLECTION));
        batch.set(docRef, {
          ...unitData,
          createdAt: new Date(),
          updatedAt: new Date(),
          status: unitData.status || 'available'
        });
        createdUnits.push({ id: docRef.id, ...unitData });
      }

      await batch.commit();
      return createdUnits;
    } catch (error) {
      throw error;
    }
  },

  // Get property hierarchy (groups with their units)
  async getPropertyHierarchy(companyId) {
    try {
      const groups = await this.getPropertyGroupsByCompany(companyId);
      const hierarchy = [];

      for (const group of groups) {
        const units = await this.getUnitsByGroup(group.id);
        hierarchy.push({
          ...group,
          units
        });
      }

      return hierarchy;
    } catch (error) {
      throw error;
    }
  }
}; 